"use client";

import { forwardRef, useState } from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "./skeleton";

/* ── Table root ─────────────────────────────────────────────── */

function Table({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <div className="relative w-full overflow-auto rounded-[var(--radius-lg)] border border-[var(--color-border-default)]">
      <table
        data-slot="table"
        className={cn("w-full caption-bottom text-[length:var(--font-size-sm)]", className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      className={cn(
        "bg-[var(--color-bg-muted)] [&_tr]:border-b [&_tr]:border-[var(--color-border-default)]",
        className
      )}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      className={cn(
        "border-b border-[var(--color-border-subtle)]",
        "transition-colors duration-[var(--duration-fast)]",
        "hover:bg-[var(--color-bg-muted)]",
        "data-[state=selected]:bg-[var(--color-primary-subtle)]",
        className
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      className={cn(
        "px-[var(--space-4)] py-[var(--space-3)] text-[var(--color-text-primary)] align-middle",
        className
      )}
      {...props}
    />
  );
}

/* ── Sortable header cell ───────────────────────────────────── */

type SortDir = "asc" | "desc" | null;

interface TableSortHeaderProps extends React.ComponentProps<"th"> {
  sorted?: SortDir;
  onSort?: () => void;
}

const TableSortHeader = forwardRef<HTMLTableCellElement, TableSortHeaderProps>(
  ({ className, sorted, onSort, children, ...props }, ref) => {
    const SortIcon =
      sorted === "asc"
        ? ChevronUp
        : sorted === "desc"
          ? ChevronDown
          : ChevronsUpDown;

    return (
      <th
        ref={ref}
        className={cn(
          "px-[var(--space-4)] py-[var(--space-3)] text-left align-middle",
          "text-[length:var(--font-size-xs)] font-[number:var(--font-weight-semibold)] uppercase tracking-[var(--letter-spacing-wide)]",
          "text-[var(--color-text-muted)]",
          onSort && "cursor-pointer select-none",
          className
        )}
        onClick={onSort}
        aria-sort={
          sorted === "asc"
            ? "ascending"
            : sorted === "desc"
              ? "descending"
              : undefined
        }
        {...props}
      >
        <span className="inline-flex items-center gap-1">
          {children}
          {onSort && (
            <SortIcon
              className={cn(
                "size-3.5",
                sorted ? "text-[var(--color-text-primary)]" : "text-[var(--color-text-muted)]"
              )}
              aria-hidden="true"
            />
          )}
        </span>
      </th>
    );
  }
);
TableSortHeader.displayName = "TableSortHeader";

/* ── Empty state ────────────────────────────────────────────── */

function TableEmpty({
  colSpan,
  children,
}: {
  colSpan: number;
  children: React.ReactNode;
}) {
  return (
    <tr>
      <td
        colSpan={colSpan}
        className="px-[var(--space-4)] py-[var(--space-12)] text-center text-[var(--color-text-muted)]"
      >
        {children}
      </td>
    </tr>
  );
}

/* ── Loading skeleton rows ──────────────────────────────────── */

function TableSkeleton({
  columns,
  rows = 5,
}: {
  columns: number;
  rows?: number;
}) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <tr
          key={rowIdx}
          className="border-b border-[var(--color-border-subtle)]"
        >
          {Array.from({ length: columns }).map((_, colIdx) => (
            <td key={colIdx} className="px-[var(--space-4)] py-[var(--space-3)]">
              <Skeleton className="h-4 w-full" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/* ── Pagination ─────────────────────────────────────────────── */

interface TablePaginationProps {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  className?: string;
}

function TablePagination({
  page,
  pageCount,
  onPageChange,
  className,
}: TablePaginationProps) {
  return (
    <div
      className={cn(
        "flex items-center justify-between px-[var(--space-4)] py-[var(--space-3)]",
        "border-t border-[var(--color-border-default)]",
        "text-[length:var(--font-size-sm)] text-[var(--color-text-muted)]",
        className
      )}
    >
      <span>
        Page {page} of {pageCount}
      </span>
      <div className="flex gap-[var(--space-1)]">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="rounded-[var(--radius-md)] px-[var(--space-3)] py-[var(--space-1)] border border-[var(--color-border-default)] text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-bg-muted)] disabled:opacity-50 disabled:pointer-events-none"
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount}
          className="rounded-[var(--radius-md)] px-[var(--space-3)] py-[var(--space-1)] border border-[var(--color-border-default)] text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-bg-muted)] disabled:opacity-50 disabled:pointer-events-none"
        >
          Next
        </button>
      </div>
    </div>
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableSortHeader,
  TableEmpty,
  TableSkeleton,
  TablePagination,
};
export type { SortDir, TableSortHeaderProps, TablePaginationProps };
